import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SystemAnnouncementsAPI from "./services/SystemAnnouncementsAPI";
import { useUser } from "./context/UserContext";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import {
  filterAlertsByLevel,
  getUserAlertLevel,
} from "./utils/AlertLevelsUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faGraduationCap,
} from "@fortawesome/free-solid-svg-icons";
import "./css/Announcements.css";

const announcementIntervalMs = 30 * 60 * 1000; // 30 minutes

/**
 * Announcements Component - Displays announcements in the hero bar
 *
 * Announcements are platform-level notifications like:
 * - Scheduled maintenance notices
 * - New feature announcements
 * - Service status updates
 *
 * This is separate from the Alerts Intelligence Hub which shows
 * educational content about scams and fraud.
 */
const Announcements = ({ onVisibilityChange }) => {
  const navigate = useNavigate();
  const { isDeactivated } = useFeatureAccess();
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [userAlertLevel, setUserAlertLevel] = useState(getUserAlertLevel());
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textWrapperRef = useRef(null);
  const textRef = useRef(null);
  const { loggedInUser } = useUser();

  // Check if announcement text overflows its container on mobile
  const checkOverflow = useCallback(() => {
    if (window.innerWidth <= 768 && textWrapperRef.current && textRef.current) {
      const wrapperWidth = textWrapperRef.current.clientWidth;
      const textWidth = textRef.current.scrollWidth;
      setIsOverflowing(textWidth > wrapperWidth);
    } else {
      setIsOverflowing(false);
    }
  }, []);

  // Re-check overflow when announcement changes or window resizes
  useEffect(() => {
    // Reset first so text renders without scrolling class, then measure
    setIsOverflowing(false);
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        checkOverflow();
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [currentIndex, filteredAnnouncements, checkOverflow]);

  useEffect(() => {
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [checkOverflow]);

  // Fetch announcements based on user authentication state
  const fetchAnnouncements = async () => {
    try {
      setIsLoading(true);

      // Fetch active announcements (for all users)
      console.log("🌐 Announcements: Fetching active announcements");
      const activeAnnouncements =
        await SystemAnnouncementsAPI.getActiveAnnouncements();
      setAnnouncements(activeAnnouncements);
      setError(null);
    } catch (err) {
      console.error("❌ Announcements: Error fetching announcements:", err);
      setError("Failed to load announcements");
      setAnnouncements([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch and periodic refresh
  useEffect(() => {
    fetchAnnouncements();

    // Refresh announcements every 30 minutes
    const interval = setInterval(fetchAnnouncements, announcementIntervalMs);
    return () => clearInterval(interval);
  }, [loggedInUser]); // Re-fetch when login state changes

  // Filter announcements based on user's level preference
  useEffect(() => {
    console.log(
      `🎛️ Announcements: Filtering announcements for level '${userAlertLevel}'`,
    );
    const filtered = filterAlertsByLevel(announcements, userAlertLevel);
    setFilteredAnnouncements(filtered);
    console.log(
      `📊 Announcements: ${announcements.length} total → ${filtered.length} filtered`,
    );
  }, [announcements, userAlertLevel]);

  // Watch for user login changes and reset dismiss state
  useEffect(() => {
    if (loggedInUser) {
      console.log(
        "👤 Announcements: User logged in, resetting dismissal state",
      );
      setIsDismissed(false);
    }
  }, [loggedInUser]);

  // Listen for alert level preference changes
  useEffect(() => {
    const handleAlertLevelChange = (event) => {
      const newLevel = event.detail.alertLevel;
      console.log(`🔄 Announcements: Alert level changed to '${newLevel}'`);
      setUserAlertLevel(newLevel);
      setIsDismissed(false); // Reset dismissal when level changes
    };

    window.addEventListener("alertLevelChanged", handleAlertLevelChange);
    return () => {
      window.removeEventListener("alertLevelChanged", handleAlertLevelChange);
    };
  }, []);

  // Cycle through announcements every 13 seconds if multiple exist
  useEffect(() => {
    if (filteredAnnouncements.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex(
        (prevIndex) => (prevIndex + 1) % filteredAnnouncements.length,
      );
    }, 13000);

    return () => clearInterval(interval);
  }, [filteredAnnouncements.length]);

  // Reset currentIndex when filtered announcements change to prevent out-of-bounds access
  useEffect(() => {
    if (filteredAnnouncements.length > 0) {
      setCurrentIndex(0); // Always reset to first announcement when filtered list changes
    }
  }, [filteredAnnouncements]);

  // Notify parent about visibility changes
  useEffect(() => {
    const isVisible =
      !isLoading && filteredAnnouncements.length > 0 && !isDismissed;
    if (onVisibilityChange) {
      onVisibilityChange(isVisible);
    }
  }, [
    isLoading,
    filteredAnnouncements.length,
    isDismissed,
    onVisibilityChange,
  ]);

  const handleAnnouncementClick = (e) => {
    // Handle internal link clicks with app navigation
    if (e.target.tagName === "A") {
      const href = e.target.getAttribute("href");
      // Check if it's an internal app link (starts with /app/)
      if (href && href.startsWith("/app/")) {
        e.preventDefault();
        // Map URL paths to hero content names
        const pathToContent = {
          "/app/alerts-hub": "Alerts Intelligence Hub",
          "/app/training": "Training Center",
          "/app/dashboard": "Dashboard",
        };
        const contentName = pathToContent[href];
        if (contentName) {
          // Use the SidePanel's hero content mechanism via localStorage + custom event
          // This remains the best approach for same-window, same-SPA navigation
          localStorage.setItem("requestedHeroContent", contentName);
          window.dispatchEvent(
            new CustomEvent("heroContentRequested", { detail: contentName }),
          );
        } else {
          // Use React Router navigation for /app/ paths with slug-based deep links
          navigate(href);
        }
      }
      // External links will work normally
      return;
    }
    // Only cycle if there are multiple filtered announcements
    if (filteredAnnouncements.length > 1) {
      setCurrentIndex(
        (prevIndex) => (prevIndex + 1) % filteredAnnouncements.length,
      );
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  // Don't render for deactivated member users
  if (isDeactivated) return null;

  // Don't render anything if no filtered announcements, still loading, or dismissed
  if (isLoading || filteredAnnouncements.length === 0 || isDismissed) {
    return null;
  }

  const currentAnnouncement = filteredAnnouncements[currentIndex];

  // Safety check: if currentAnnouncement is undefined, don't render
  if (!currentAnnouncement) {
    console.warn(
      "⚠️ Announcements: currentAnnouncement is undefined, currentIndex:",
      currentIndex,
      "filteredAnnouncements.length:",
      filteredAnnouncements.length,
    );
    return null;
  }

  const getAnnouncementClassName = (type) => {
    const baseClass = "announcement-container";
    // Safety check for undefined type and normalize to lowercase
    const announcementType = (type || "info").toLowerCase();
    let className;
    switch (announcementType) {
      case "warning":
        className = `${baseClass} announcement-warning`;
        break;
      case "error":
        className = `${baseClass} announcement-error`;
        break;
      case "success":
        className = `${baseClass} announcement-success`;
        break;
      case "info":
      default:
        className = `${baseClass} announcement-info`;
        break;
    }

    // Add clickable class if there are multiple filtered announcements
    if (filteredAnnouncements.length > 1) {
      className += " clickable";
    }

    return className;
  };

  return (
    <div
      className={getAnnouncementClassName(currentAnnouncement.type)}
      onClick={handleAnnouncementClick}
      style={{
        cursor: filteredAnnouncements.length > 1 ? "pointer" : "default",
      }}
    >
      <div className="announcement-content">
        <div
          className={`announcement-text-wrapper${isOverflowing ? " scrolling" : ""}`}
          ref={textWrapperRef}
        >
          <div
            ref={textRef}
            className={`announcement-text${isOverflowing ? " scrolling-text" : ""}`}
            dangerouslySetInnerHTML={{
              __html: currentAnnouncement.message || "No message available",
            }}
          />
        </div>
        {/* Linked content action buttons */}
        {(currentAnnouncement.relatedAlertIds?.length > 0 ||
          currentAnnouncement.relatedTrainingUuids?.length > 0) && (
          <div className="announcement-actions">
            {currentAnnouncement.relatedAlertIds?.length > 0 && (
              <button
                className="announcement-action-btn announcement-action--alert"
                onClick={(e) => {
                  e.stopPropagation();
                  const alertId = currentAnnouncement.relatedAlertIds[0];
                  localStorage.setItem(
                    "requestedHeroContent",
                    "Alerts Intelligence Hub",
                  );
                  window.dispatchEvent(
                    new CustomEvent("heroContentRequested", {
                      detail: "Alerts Intelligence Hub",
                    }),
                  );
                }}
                title="View related alert"
              >
                <FontAwesomeIcon icon={faShieldAlt} />
                <span>View Alert</span>
              </button>
            )}
            {currentAnnouncement.relatedTrainingUuids?.length > 0 && (
              <button
                className="announcement-action-btn announcement-action--training"
                onClick={(e) => {
                  e.stopPropagation();
                  const uuid = currentAnnouncement.relatedTrainingUuids[0];
                  localStorage.setItem(
                    "requestedHeroContent",
                    "Training Center",
                  );
                  window.dispatchEvent(
                    new CustomEvent("heroContentRequested", {
                      detail: "Training Center",
                    }),
                  );
                }}
                title="Start related training"
              >
                <FontAwesomeIcon icon={faGraduationCap} />
                <span>Training</span>
              </button>
            )}
          </div>
        )}
        <div className="announcement-right-section">
          {filteredAnnouncements.length > 1 && (
            <div className="announcement-indicator">
              {currentIndex + 1} / {filteredAnnouncements.length}
            </div>
          )}
          <button
            className="announcement-dismiss-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            aria-label="Dismiss announcement"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

export default Announcements;
